import { useMsal } from "@azure/msal-react";
import {
    AnimationStyles, ChoiceGroup, DefaultButton, Dropdown, FontWeights, IDropdownOption, IPanelProps, IRenderFunction, mergeStyles, MessageBar, MessageBarType, Panel, PanelType,
    PrimaryButton, Separator, Spinner, SpinnerSize, Stack, Text, TextField, Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import SimpleCard, { ECardIcon } from "components/controls/SimpleCard";
import { createPlan, panelButtonsStyles, sectionStackTokens } from "global/Application";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdatePlan, importPlansAndBenefits } from "global/Reducer/dataSlice";
import { JsonTransformer } from "global/Transform/JsonTransformer";
import { timestamp, updateArrayReplace } from "global/Utility/TSFunctions";
import {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import { IPanelEvent, IPickItem, IValueChoiceGroupOption } from "model/Extension";
import {
    IBenefit as FAM_IBenefit,
    IOptionSetValue as FAM_IOptionSetValue,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import SnapshotLookup from "./Controls/SnapshotLookup";
import PlanCard from "./PlanCard";

interface IConfirm {
    planSelected?: FAM_IPlan | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    currentBenefit?: FAM_IBenefit

    variant?: "pick" | "edit"
}

const enum EView {
    entry,
    add,
    selectPlan,
    copy,
    import,
};

const fadeIn = mergeStyles(AnimationStyles.fadeIn500, {});

const PlanWizard = (props: IProps) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<Boolean>(false);

    // current view state/stages
    const [view, setView] = useState<EView | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [optionEntry, setOptionEntry] = useState<IValueChoiceGroupOption<EView> | null>(null);

    const [planNew, setPlanNew] = useState<FAM_IPlan | null>(null);
    const [planSelected, setPlanSelected] = useState<FAM_IPlan | null>(null);

    const [importSnapshot, setImportSnapshot] = useState<IXrmTypes_bcbs_groupconfigurationsnapshot | null>(null);

    const [importPlans, setImportPlans] = useState<IPickItem<FAM_IPlan>[] | null>(null);
    const [importBenefits, setImportBenefits] = useState<IPickItem<FAM_IBenefit>[] | null>(null);

    const optionsSource: IValueChoiceGroupOption<EView>[] = [
        { key: 'add', text: 'Add New', data: EView.add, disabled: props.variant !== "edit" },
        { key: 'pick', text: 'Pick - From Current Configuration', data: EView.selectPlan, disabled: props.variant !== "pick" },
        { key: 'copy', text: 'Copy/Clone - From Current Configuration', data: EView.copy, disabled: props.variant !== "edit" },
        { key: 'import', text: 'Copy/Clone - From External Configuration/BlueRelationship', data: EView.import, disabled: props.variant !== "edit" },
    ];

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    //TODO: remove dependency
    useEffect(() => {
        // any props that we need to build state on component mount?
        // reset on start
        if (props.isOpen) {

            if (props.variant === 'pick') {
                setView(EView.selectPlan);
                setOptionEntry(optionsSource.find(t => t.data === EView.selectPlan)!);
            } else {
                setView(EView.entry);
                setOptionEntry(null);
            }

            setPlanNew(null);
            setPlanSelected(null);
            setImportBenefits(null);
            setImportPlans(null);
        }

    }, [props.isOpen]);


    useEffect(() => {
        if (view === EView.copy) {

        }
        else if (importSnapshot) {
            (async _ => {
                setLoading(true);

                try {
                    const result = await dataSource.downloadGroupConfigurationSnapshot(importSnapshot);

                    if (!result?.json)
                        throw new Error('Snapshot not found!');

                    const transformer = JsonTransformer.getTransformerForCopy(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToPlans: true, applyToBenefits: true }
                    );

                    await transformer.transformAsync(result.json, importSnapshot);

                    const data = result.json;

                    const plans: IPickItem<FAM_IPlan>[] = [];
                    data.plans.forEach(eachPlan => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachPlan.deactivated)
                            return;

                        plans.push({
                            item: eachPlan,
                            selected: false
                        });
                    });

                    setImportPlans(plans);

                    const benefits = data.benefits.map(t => {
                        return {
                            item: t,
                            selected: false
                        } as (IPickItem<FAM_IBenefit>)
                    })

                    setImportBenefits(benefits);

                    console.log('PlanWizard> snapshot loaded.', importSnapshot.bcbs_filename || importSnapshot.bcbs_snapshot_name, data);


                } catch (error) {
                    console.error('parsing json failed for snapshot.')
                    throw new Error('snapshot parsing failed');
                } finally {
                    setLoading(false);
                }
            })();
        }

    }, [importSnapshot]);

    function onRenderEntry(): JSX.Element {
        return <div>
            <ChoiceGroup
                options={optionsSource}
                label="Choose Source"
                required={true}
                selectedKey={optionEntry?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionEntry(option ?? null);
                }}
            />
        </div >;
    }

    function onRenderEntryFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null}
                onClick={() => {
                    const view = optionEntry?.data;

                    if (view === EView.add) {
                        if (!planNew) {

                            const plan = createPlan(
                                props.state,
                                null,
                                null,
                                props.state.data.json.grandFathered,
                                props.state.data.json.fundingType,
                            )

                            setPlanNew(plan);
                        }
                    }

                    setView(optionEntry?.data ?? null);

                }}
            />
            <DefaultButton text='Cancel' onClick={() => props.onCancel?.()} />
        </div>
    }

    function onRenderNew(): JSX.Element {

        const isGrandFathered = true;
        const isDualFunded = props.state.data.json.dualFunded?.value ?? false;

        if (!planNew)
            return <div>Plan not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <TextField
                        label='Name'
                        value={planNew.name ?? ''}
                        onChange={(event, value) => {
                            setPlanNew(planNew => { return { ...planNew!, name: value ?? '' }; });
                        }}
                    />
                    <div>
                        <Dropdown
                            label={"Funding Type"}
                            options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                            disabled={!isDualFunded}
                            selectedKey={planNew.fundingType?.value}
                            onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                                setPlanNew(planNew => { return { ...planNew!, fundingType: option?.data ?? null }; });
                            }}
                        />
                        {!isDualFunded &&
                            <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Group configuration has not been setup for dual funding.</Text>
                        }
                    </div>

                    <Dropdown
                        label={"Grandfathered"}
                        options={props.state.data.config.cache.options_bcbs_grandfatheredcode ?? []}
                        disabled={!isGrandFathered}
                        selectedKey={planNew.grandFathered?.value}
                        onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                            const t: FAM_IPlan = { ...planNew };
                            setPlanNew(planNew => { return { ...planNew!, grandFathered: option?.data ?? null }; });
                        }}
                    />

                    <Toggle
                        label={"Billing Only Plan"}
                        checked={planNew.billingOnlyPlan ?? false}
                        onChange={(_, checked) => {
                            setPlanNew(planNew => { return { ...planNew!, billingOnlyPlan: checked ?? false }; });
                        }}
                    />

                </Stack>


            </div>
        );
    }

    function onRenderNewFooter(): JSX.Element {
        if (!planNew)
            return <div>Plan not available!</div>;

        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={planNew.name === ''}
                onClick={() => {
                    const _planNew: FAM_IPlan = JSON.parse(JSON.stringify(planNew));
                    if (optionEntry?.data === EView.copy) {
                        _planNew.id = uuidv4();
                        _planNew.feesAndPremiumsRef = null;
                        _planNew.fields.forEach(eachField => {
                            // update each field's main id, and attributes id's
                            eachField.id = uuidv4();
                            eachField.values.forEach(eachFlexibleValue => {
                                eachFlexibleValue.id = uuidv4();
                            });
                        });
                    }

                    dispatch(addUpdatePlan([_planNew]));
                    setPlanNew(null);
                    setView(EView.entry);
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel' onClick={() => setView(EView.entry)} />
        </div>;
    }

    function onRenderCopy(): JSX.Element {
        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Copy Plan
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {props.state.data.json.plans.map(eachPlan => {

                        if (eachPlan.deactivated)
                            return <></>;

                        return (
                            <SimpleCard
                                key={eachPlan.id}
                                description={eachPlan.name ?? ""}
                                selected={eachPlan.id === planNew?.id}
                                icons={ECardIcon.copy}
                                onClick={() => {
                                    if (!planSelected || planSelected.id !== eachPlan.id) {
                                        const t: FAM_IPlan = {
                                            ...eachPlan,
                                            name: `${eachPlan.name} - Copy - ${timestamp()}`,
                                        };

                                        setPlanNew(t);
                                        setPlanSelected(eachPlan);
                                    }
                                    else {
                                        setPlanNew(null);
                                        setPlanSelected(null);
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>

            </div>
        )
    }

    function onRenderCopyFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={planSelected === null || planSelected?.name === ""}
                onClick={() => {
                    setView(EView.add)
                }}
            />
            <DefaultButton text='Cancel' onClick={() => setView(EView.entry)} />
        </div>;
    }

    function onRenderImport(): JSX.Element {
        return <Stack tokens={{ childrenGap: '10' }}>
            <SnapshotLookup
                onPick={async (event) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(event?.id!);
                    setImportSnapshot(snapshot);
                }}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy Plans
            </Text>

            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {importPlans?.map(eachPlan => {

                    if (eachPlan.item.deactivated)
                        return <></>;

                    return (
                        <SimpleCard
                            key={eachPlan.item.id}
                            description={eachPlan.item.name ?? ""}
                            selected={eachPlan.selected}
                            icons={ECardIcon.copy}
                            onClick={() => {
                                const newPlan = { ...eachPlan, selected: !eachPlan.selected }
                                const newState = updateArrayReplace(importPlans, eachPlan, newPlan);
                                setImportPlans(newState);

                                const benefits = [...importBenefits!];

                                // lets pick benefits that are associated with plans
                                benefits
                                    .filter(b => newPlan.selected && newPlan.item.benefitRefs
                                        .some(br => br.id === b.item.id))
                                    .forEach(eBen => {
                                        eBen.selected = true;
                                    });

                                setImportBenefits(benefits);
                            }}
                        />
                    );
                })}
            </Stack>

            <Separator />
            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy Benefits
            </Text>

            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {importBenefits?.map(t => {
                    return (
                        <SimpleCard
                            key={t.item.id}
                            description={t.item.name ?? ""}
                            selected={t.selected}
                            icons={ECardIcon.copy}
                            onClick={() => {
                                const newState = updateArrayReplace(importBenefits, t, { ...t, selected: !t.selected });
                                setImportBenefits(newState);
                            }}
                        />
                    );
                })}
            </Stack>
        </Stack>
    }

    function onRenderImportFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!importPlans?.some(t => t.selected)}
                onClick={() => {

                    // pick selected plans and step in on selection generic
                    const plans = importPlans?.filter(q => q.selected === true).map(q => q.item)
                    const benefits = importBenefits?.filter(q => q.selected === true).map(q => q.item)

                    dispatch(importPlansAndBenefits({
                        config: props.state.data.config,
                        plans: plans,
                        benefits: benefits
                    }));

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => props.onDismiss?.()}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderSelectPlan(): JSX.Element {
        return <Stack className={fadeIn} tokens={{ childrenGap: '15px' }} >
            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Choose Plan
            </Text>
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
            >
                {props.state.data.json.plans.map(eachPlan => {
                    if (eachPlan.deactivated)
                        return <></>;

                    const selected = eachPlan === planSelected;

                    return <PlanCard
                        key={eachPlan.id}
                        plan={eachPlan}
                        state={props.state}
                        selected={selected}
                        onClick={(event) => {
                            setPlanSelected(eachPlan);
                        }}
                    />;
                })}
            </Stack>
        </Stack>;
    }

    function onRenderSelectPlanFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null}
                onClick={() => {
                    props.onConfirm?.({ value: { planSelected: structuredClone(planSelected) } });
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => props.onCancel?.()}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (props, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntry
                break;

            case EView.add:
                onRender = onRenderNew
                break;

            case EView.selectPlan:
                onRender = onRenderSelectPlan
                break;

            case EView.copy:
                onRender = onRenderCopy
                break;

            case EView.import:
                onRender = onRenderImport
                break;
        }

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                }
                {onRender()}
            </div>
        );
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (props, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter
                break;

            case EView.add:
                onRender = onRenderNewFooter
                break;


            case EView.selectPlan:
                onRender = onRenderSelectPlanFooter
                break;


            case EView.copy:
                onRender = onRenderCopyFooter
                break;

            case EView.import:
                onRender = onRenderImportFooter
                break;
        }

        return onRender();

    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onCancel}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Plan | Manage"
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(PlanWizard);

